import { Box, Button, Chip, Divider, List, ListItem, ListItemButton, ListItemText, Paper, Stack, SxProps, Theme, Typography } from "@mui/material";
import { CSSProperties } from "react";
import NavLink, { NavButton } from "../../components/NavLink";
import AccentPaper from "../../components/Paper";
import { AbstractTypedDataForm } from "../../components/data/DataForm";
import DataTable from "../../components/data/DataTable";
import { Bill, MeasureBillDto, PeriodicServiceBill, ServiceBill } from "../../models/Types";
import { formatDT, isMobile, moneyFormat, numberFormat, periodNumToText } from "../../utils";
import { houseStore } from "../houses/HouseCombo";
import { periodicServiceStore } from "../project/periodicService/PeriodicServiceCombo";
import { serviceStore } from "../project/service/ServiceCombo";
import { tariffStore } from "../project/tariff/TariffCombo";
import { NavLinkComponent } from "../../Router";
import { ChevronRight } from "@mui/icons-material";

const styles = {
    paper: { display: 'flex', flexDirection: 'row', alignItems: 'center', flex: 'none' } as CSSProperties
}

export function billParse(d?: any) {
    const dto = (d || {}) as Bill;
    d?.CreateDate && (dto.CreateDate = new Date(d.CreateDate));
    return dto;
}

export function billDetailParse<T>(d: T) {
    const dto = d as any;
    dto.CreateDate && (dto.CreateDate = new Date(dto.CreateDate));
    return d
}

export class BillForm extends AbstractTypedDataForm<Bill, {}, { houseId?: number }> {
    protected getTypeId(): string {
        return 'Bill';
    }
    protected getTypeName(): React.ReactNode {
        return 'Начисление';
    }

    buildButtons() {
        return [];
    }

    componentDidMount(): void {
        super.componentDidMount();
        this.unlisten.push(tariffStore.onLoad(() => this.forceUpdate()));
        this.unlisten.push(serviceStore.onLoad(() => this.forceUpdate()));
        this.unlisten.push(periodicServiceStore.onLoad(() => this.forceUpdate()));
    }

    responseToState(response: any) {
        var res = super.responseToState(response);
        res.entity?.MeasureBills?.forEach((x: MeasureBillDto) => {
            x.Id = x.Id || x.TariffId;
            x.CreateDate && (x.CreateDate = new Date(x.CreateDate));
        });
        return res;
    }

    protected createEntity() {
        const result = billParse();
        result.MeasureBills = [];
        return result;
    }

    protected getFormStyle(): SxProps<Theme> | undefined {
        return {
            ...super.getFormStyle(),
            flexDirection: 'column',
            alignItems: 'stretch',
            overflow: isMobile() ? 'auto' : 'hidden'
        };
    }

    buildMobileItems(entity?: Bill | undefined): React.ReactNode {
        return <>
            {entity?.PeriodNum ? <Typography variant="h2" display="flex" textTransform="capitalize" justifyContent="center" mx={2} my={1}>
                {periodNumToText(entity?.PeriodNum)}
            </Typography> : <Typography variant="body2" display="flex" justifyContent="center" mx={2} my={1} alignItems="baseline">
                {formatDT(entity?.CreateDate)}</Typography>}

            <Typography variant="h1" display="flex" justifyContent="center" m={2}>
                {moneyFormat(entity?.Amount)}
            </Typography>

            <Box m={2} display="flex" gap={2}>
                {/*<Button variant="outlined" sx={{ flex: 1 }}>Квитанция</Button>*/}
                {entity?.PaymentId || !entity?.Amount ? null :
                    <NavButton variant="contained" sx={{ flex: 1 }} route={[['page', 'payment'], ['id', 'new'], ['bill', entity.Id]]}>Оплатить</NavButton>}
            </Box>

            {entity?.PaymentId ?
                <AccentPaper component={NavLinkComponent}
                    sx={{ mx: 2 }}
                    route={[['page', 'payment'], ['id', entity.PaymentId]] }
                    style={{ flexDirection: 'row', alignItems: 'center', flex: 'none', textDecoration: 'none', gap:1 }}
                    elevation={1}>
                    <Typography variant="h3" flex={1}>Платеж №{entity.PaymentId}</Typography>
                    <Chip size="small" color="success" label="Оплачено" />
                    <ChevronRight color="primary"/>
                </AccentPaper> : null}

            {entity?.MeasureBills?.length ? <Paper style={{ flex: 'none', padding: 0 }} sx={{ mx: 2 }}>
                <Typography variant="h3" p={2} pb={0}>Начислено по тарифам</Typography>
                <List disablePadding>
                    {entity.MeasureBills.map(billDetailParse).map((x, i) => {
                        const tariff = tariffStore.data?.find(t => t.Id == x.TariffId);
                        return <>{i ? <Divider /> : null}
                            <ListItemButton>
                                <ListItemText primary={tariff?.Value} secondary={numberFormat(x.ValueDiff, ' ' + tariff?.Obj?.Unit)} />
                                <ListItemText sx={{ textAlign: 'right' }} primary={x.Sum} secondary={moneyFormat(x.Price)} />
                            </ListItemButton>
                        </>
                    })}
                </List>
            </Paper> : null}

            {entity?.ServiceBills?.length ? <Paper style={{ flex: 'none', padding: 0 }} sx={{ mx: 2 }}>
                <Typography variant="h3" p={2} pb={0}>Начислено по услугам</Typography>
                <List disablePadding>
                    {entity.ServiceBills.map(billDetailParse).map((x, i) => {
                        const service = serviceStore.data?.find(t => t.Id == x.ServiceId)?.Obj;
                        return <>{i ? <Divider /> : null}
                            <ListItemButton>
                                <ListItemText primary={service?.Name} secondary={numberFormat(x.Count, service?.Unit)} />
                                <ListItemText sx={{ textAlign: 'right' }} primary={x.Sum} secondary={moneyFormat(x.Price)} />
                            </ListItemButton>
                        </>
                    })}
                </List>
            </Paper> : null}

            {entity?.PeriodicServices?.length ? <Paper style={{ flex: 'none', padding: 0 }} sx={{ mx: 2 }}>
                <Typography variant="h3" p={2} pb={0}>Начислено по регулярным услугам</Typography>
                <List disablePadding>
                    {entity.PeriodicServices.map(billDetailParse).map((x, i) => <>{i ? <Divider /> : null}
                        <ListItemButton>
                            <ListItemText primary={periodicServiceStore.data?.find(t => t.Id == x.PeriodicServiceId)?.Value} secondary={x.Value} />
                            <ListItemText sx={{ textAlign: 'right' }} primary={x.Sum} secondary={x.Price} />
                        </ListItemButton>
                    </>)}
                </List>
            </Paper> : null}
        </>;
    }

    protected buildItems(entity?: Bill | undefined): React.ReactNode {
        if (isMobile()) {
            return this.buildMobileItems(entity);
        }

        const house = houseStore.data?.find(x => x.Id === entity?.HouseId);
        return <>
            <Paper style={{ padding: 0 }}>
                <div/>
                <Box display="flex" flexDirection="row" gap={2} justifyContent="space-between" px={2} overflow="auto" style={styles.paper}>
                    <Stack>
                        <Typography variant="subtitle2">Адрес</Typography>
                        {house?.Value}
                    </Stack>

                    <Stack>
                        <Typography variant="subtitle2">Счет</Typography>
                        {house?.Id}
                    </Stack>

                    {entity?.PeriodNum ? <Stack>
                        <Typography variant="subtitle2">Период</Typography>
                        {periodNumToText(entity.PeriodNum)}
                    </Stack> : null}

                    <Stack>
                        <Typography variant="subtitle2">Сумма</Typography>
                        {entity?.Amount ? numberFormat(entity.Amount) : undefined}
                    </Stack>

                    <Stack gap={2}>
                        <Chip size="small" color={entity?.PaymentId ? 'success' : 'error'} label={(entity?.PaymentId ? 'О' : 'Не о') + 'плачено'} />

                        <NavButton variant="contained" route={[['page', 'payment'], ['id', 'new'], ['bill', entity?.Id]]}>Оплатить</NavButton>
                    </Stack>
                </Box>

                {entity?.MeasureBills?.length ? <DataTable<MeasureBillDto>
                    style={{ flex: 1, overflow: 'auto' }}
                    data={entity.MeasureBills.map(billDetailParse)}
                    showFilter={false}
                    columns={[{
                        name: "TariffId",
                        label: 'Ресурс',
                        store: tariffStore,
                        //filter: (onChange) => <TariffCombo onChange={e => onChange(e.target.value)} />,
                        renderer: (val, obj) => <NavLink route={[['page', 'project/tariff'], ['id', val]]}>{tariffStore.data?.find(x => x.Id == val)?.Value}</NavLink>
                    }, {
                        name: "CreateDate",
                        label: 'Дата подачи',
                        type: 'date'
                    }, {
                        name: "Value",
                        label: 'Показание',
                        renderer: (val, obj) => <NavLink route={[['page', 'measure'], ['id', obj.MeasureId]]}>{val}</NavLink>
                    }, {
                        name: "ValueDiff",
                        label: 'Изменение'
                    }, {
                        name: "Price",
                        label: 'Тариф'
                    }, {
                        name: "Sum",
                        label: 'Сумма'
                    }]}
                /> : null}

                {entity?.ServiceBills?.length ? <DataTable<ServiceBill>
                    style={{ flex: 1, overflow: 'auto' }}
                    data={entity.ServiceBills.map(billDetailParse)}
                    showFilter={false}
                    columns={[{
                        name: "ServiceId",
                        label: 'Услуга',
                        store: serviceStore,
                        //filter: (onChange) => <TariffCombo onChange={e => onChange(e.target.value)} />,
                        renderer: (val, obj) => <NavLink route={[['page', 'project/service'], ['id', val]]}>{serviceStore.data?.find(x => x.Id == val)?.Obj?.Name}</NavLink>
                    }, {
                        name: "CreateDate",
                        label: 'Дата оказания',
                        type: 'date'
                    }, {
                        name: "Count",
                        label: 'Количество'
                    }, {
                        name: "Price",
                        label: 'Цена'
                    }, {
                        name: "Sum",
                        label: 'Сумма'
                    }]}
                /> : null}

                {entity?.PeriodicServices?.length ? <DataTable<PeriodicServiceBill>
                    style={{ flex: 1, overflow: 'auto' }}
                    data={entity.PeriodicServices.map(billDetailParse)}
                    showFilter={false}
                    columns={[{
                        name: "PeriodicServiceId",
                        label: 'Услуга',
                        store: periodicServiceStore,
                        //filter: (onChange) => <TariffCombo onChange={e => onChange(e.target.value)} />,
                        renderer: (val, obj) => <NavLink route={[['page', 'project/periodicService'], ['id', val]]}>
                            {periodicServiceStore.data?.find(x => x.Id == val)?.Obj?.Name}
                        </NavLink>
                    }, {
                        name: "Value",
                        label: 'Значение'
                    }, {
                        name: "Price",
                        label: 'Цена'
                    }, {
                        name: "Sum",
                        label: 'Сумма'
                    }]}
                /> : null}

                <div />
            </Paper>
        </>;
    }
}